import type { ComponentPropsWithRef, JSX, MouseEvent } from "react";
import { createElement } from "react";
import { css, styled } from "styled-components";

import {
  BookmarkFilledIcon,
  BookmarkIcon,
  CrossIcon,
  DownloadIcon,
  OpenLinkIcon,
  PinOutlineIcon,
  PinSolidIcon,
  ShareIcon,
  ThumbsDownIcon,
} from "common/components/icons";
import { UIButton, type UIButtonProps } from "common/components/UIButton";
import { useTranslate } from "common/utilities/useTranslate";
import type { Term } from "domains/language/types";

type ButtonTypes = "bookmark" | "open" | "pin" | "reject" | "share" | "unpin" | "download" | "cross";

type UIActionButtonProps = UIButtonProps & {
  hideLabel?: boolean;
  href?: string;
  isActive?: boolean;
  label?: string;
  onClick?: (event: MouseEvent) => void;
  type: ButtonTypes;
};

const IconBackground = styled.div<{ type: ButtonTypes }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  width: 40px;
  cursor: pointer;
  transition:
    background-color 0.3s,
    color 0.3s;
  border-radius: 9999px;

  ${({ type }) => css`
    ${(type === "unpin" || type === "download") &&
    css`
      background-color: ${({ theme }) => theme.colors.blueDark};
      color: ${({ theme }) => theme.colors.white};
    `}

    ${type === "cross" &&
    css`
      background-color: ${({ theme }) => theme.colors.redDark};
      color: ${({ theme }) => theme.colors.white};
    `}
  `}
`;

const StyledLabelSpan = styled.span`
  color: ${({ theme }) => theme.colors.buttonListItemText};
  display: none;
  margin-left: 10px;
  transition: color 0.3s;

  @media (min-width: 600px) {
    display: inline;
  }
`;

const StyledButton = styled(UIButton)`
  font-weight: bold;
  padding: 0;
  min-width: 40px;
  min-height: 40px;
  background-color: unset;
  border-radius: 0px;
  text-transform: capitalize;
  color: ${({ theme }) => theme.colors.buttonListItemText};

  &:disabled {
    pointer-events: none;
    cursor: not-allowed;
  }

  &:focus-visible,
  &:hover,
  &:hover ${StyledLabelSpan} {
    background: none;
    color: ${({ theme }) => theme.colors.buttonNeutralActiveText};
  }

  ${({ type }) => css`
    ${(type === "unpin" || type === "download") &&
    css`
      &:focus-visible,
      &:hover ${IconBackground} {
        background-color: ${({ theme }) => theme.colors.blue};
      }
    `}

    ${type === "cross" &&
    css`
      &:focus-visible,
      &:hover ${IconBackground} {
        background-color: ${({ theme }) => theme.colors.red};
      }
    `}
  `}
`;

const getIcon = (type: ButtonTypes, isActive: boolean) => {
  const iconMap: Record<ButtonTypes, (props: ComponentPropsWithRef<"svg">) => JSX.Element> = {
    bookmark: isActive ? BookmarkFilledIcon : BookmarkIcon,
    cross: () => (
      <IconBackground type={type}>
        <CrossIcon width={18} height={18} />
      </IconBackground>
    ),
    download: () => (
      <IconBackground type={type}>
        <DownloadIcon width={20} height={20} />
      </IconBackground>
    ),
    open: OpenLinkIcon,
    pin: PinOutlineIcon,
    reject: () => <ThumbsDownIcon width={20} height={20} />,
    share: ShareIcon,
    unpin: () => (
      <IconBackground type={type}>
        <PinSolidIcon width={20} height={20} />
      </IconBackground>
    ),
  };

  return createElement(iconMap[type], {
    style: { width: 20, height: 20 },
  });
};

const getLabelText = (type: ButtonTypes, isActive: boolean) => {
  const labelTextMap: Record<ButtonTypes, Term | undefined> = {
    bookmark: isActive ? "app.bookmarked.title" : "app.bookmark.title",
    cross: undefined,
    download: "app.topic.briefing.download",
    open: "app.topic.actions.open",
    pin: "app.pinned_knowledge.pin",
    reject: "app.reject_content.reject",
    share: "app.monitor.share",
    unpin: "app.pinned_knowledge.pinned",
  };

  return labelTextMap[type];
};

export const UIActionButton = ({
  hideLabel = false,
  href,
  isActive = true,
  label,
  onClick,
  type,
  ...rest
}: UIActionButtonProps) => {
  const translate = useTranslate();
  const defaultLabelText = getLabelText(type, isActive);
  const buttonText = label ?? (defaultLabelText === undefined ? "" : translate(defaultLabelText));

  const onClickHandle = (event: MouseEvent) => {
    event.stopPropagation();
    onClick?.(event);
    if (href) window.open(href, "_blank");
  };

  return (
    <StyledButton
      {...rest}
      {...(href
        ? { forwardedAs: "a", href, target: "_blank", rel: "noopener noreferrer" }
        : { onClick: onClickHandle })}
      {...(hideLabel && { title: buttonText })}
    >
      {getIcon(type, isActive)}
      {!hideLabel && <StyledLabelSpan>{buttonText}</StyledLabelSpan>}
    </StyledButton>
  );
};
